import { ChangeEvent, DragEvent, useEffect } from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";

import { useSeo } from "~/hooks/useSeo";
import { myUseState } from "~/hooks/myUseState";
import { getClassName } from "~/utils/utils";
import { IPDPhoto } from "~/interfaces";
import { mainStore } from "~/stores/main";

import "./style.less";

interface IData {
	action: Array<IPDPhoto>;
	addons: Array<IPDPhoto>;
	formal: Array<IPDPhoto>;
	schoolWedding: Array<IPDPhoto>;
	shop: Array<IPDPhoto>;
	wedding: Array<IPDPhoto>;
}

interface IState extends IData {
	selName: string;
	isChange: boolean;
	info: string;
}

const ALL_OPTS = ["action", "addons", "formal", "schoolWedding", "shop", "wedding"];
const LS_NAME = "ivanaEditPhotos";
const LS_NAME_VERSION = "ivanaEditPhotosVersion";

export default function EditPage() {
	function getStateFromData() {
		const photosDataStore = mainStore.getState().data.photosData;
		const photosData = structuredClone(photosDataStore);
		const output: IData = {
			action: photosData.action.photos,
			addons: photosData.addons.photos,
			formal: photosData.formal.photos,
			schoolWedding: photosData.schoolWedding.photos,
			shop: photosData.shop.photos,
			wedding: photosData.wedding.photos,
		};

		return output;
	}

	function getStateFromLS() {
		const data = localStorage.getItem(LS_NAME) || "";
		const version = localStorage.getItem(LS_NAME_VERSION) || "";

		if (!version || version !== window.appVersion) {
			return {};
		}

		if (data) {
			try {
				const parsed = JSON.parse(data);
				const output: IData = parsed;

				return output;
			} catch (exc) {
				//
			}
		}

		return {};
	}

	const { state, updateState } = myUseState<IState>({
		...getStateFromData(),
		...getStateFromLS(),
		selName: "wedding",
		isChange: false,
		info: "",
	});

	function getItems() {
		switch (state.selName) {
			case "action":
				return state.action;

			case "addons":
				return state.addons;

			case "formal":
				return state.formal;

			case "schoolWedding":
				return state.schoolWedding;

			case "shop":
				return state.shop;

			case "wedding":
				return state.wedding;

			default:
		}

		return [];
	}

	function getName(name: string) {
		switch (name) {
			case "action":
				return "Akce";

			case "addons":
				return "Doplňky";

			case "formal":
				return "Společenské";

			case "schoolWedding":
				return "Polonéza";

			case "shop":
				return "Prodejna";

			case "wedding":
				return "Svatební";

			default:
		}

		return "";
	}

	function deletePhoto(photo: IPDPhoto) {
		const curItems = getItems();
		const newItems = curItems.filter(item => item.photo !== photo.photo);
		const indexes: {[key: number]: number} = {};

		for (const item of newItems) {
			if (item.page === photo.page) {
				if (!(item.page in indexes)) {
					indexes[item.page] = 1;
				}

				item.order = indexes[item.page];
				indexes[item.page]++;
			}
		}

		updateState({
			[state.selName]: newItems,
			isChange: true,
		});
	}

	function onSortEnd(oldIndex: number, newIndex: number) {
		const curItems = getItems();
		const newItems = arrayMoveImmutable(curItems, oldIndex, newIndex);
		const oldPage = curItems[oldIndex].page;
		let newPage = 0;

		if (newIndex > 0) {
			const prev = newItems[newIndex - 1];
			const page = prev.page;

			newItems[newIndex].page = page;
			newPage = page;
		}

		// mame dve stranky, jdeme upravit pozice
		const indexes: {[key: number]: number} = {};

		for (const item of newItems) {
			if (item.page === oldPage || item.page === newPage) {
				if (!(item.page in indexes)) {
					indexes[item.page] = 1;
				}

				item.order = indexes[item.page];
				indexes[item.page]++;
			}
		}

		updateState({
			[state.selName]: newItems,
			isChange: true,
		});
	}

	function getCurData() {
		const curData: IData = {
			action: state.action,
			addons: state.addons,
			formal: state.formal,
			schoolWedding: state.schoolWedding,
			shop: state.shop,
			wedding: state.wedding,
		};

		return curData;
	}

	function saveChanges() {
		const curData = getCurData();

		localStorage.setItem(LS_NAME, JSON.stringify(curData));
		localStorage.setItem(LS_NAME_VERSION, window.appVersion);
		updateState({
			info: "Změny byly uloženy",
		});
	}

	function downloadJson(json: any, fileName: string) {
		const blob = new Blob([JSON.stringify(json, null, "\t")]);
		const jsonUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = jsonUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(jsonUrl);
	}

	function exportFile() {
		const curData = getCurData();

		downloadJson(curData, "ivana-fotky.json");
		updateState({
			info: "Soubor byl exportován",
		});
	}

	function deleteAll() {
		updateState({
			...getStateFromData(),
			selName: "wedding",
			isChange: false,
			info: "Lokální změny byly smazány",
		});
		localStorage.removeItem(LS_NAME);
	}

	async function sendChanges() {
		try {
			const curData = getCurData();
			const response = await fetch(`https://makuderovi.cz/jsonTable.php`, {
				method: "POST",
				body: JSON.stringify(curData),
			});
			const jsonData = await response.json();

			if (jsonData.error === "") {
				updateState({
					info: "Změny poslány na server!",
				});
			} else {
				updateState({
					info: `Chyba při poslání na server - ${jsonData.error}`,
				});
			}
		} catch (exc) {
			/* eslint-disable no-console */
			console.log(exc);
			updateState({
				info: `Chyba při poslání na server - ${exc.message || "exception"}`,
			});
		}
	}

	function getFileData(file: File): Promise<{ data: any; error: string; }> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.addEventListener("load", () => {
				try {
					const data = reader.result.toString();
					const json = JSON.parse(data);

					resolve({
						data: json,
						error: "",
					});
				} catch (exc) {
					reject({
						data: null,
						error: "Nelze zparsovat json",
					});
				}
			});
			reader.addEventListener("error", () => {
				reject({
					data: null,
					error: "Nelze načíst soubor",
				});
			});
			reader.readAsText(file);
		});
	}

	async function onChange(event: ChangeEvent<HTMLInputElement>) {
		let info = "";

		if (event.target.files.length > 0) {
			const firstFile = event.target.files[0];

			event.target.value = null;

			try {
				const fileData = await getFileData(firstFile);

				updateState({
					...fileData.data,
				});
			} catch (exc) {
				info = exc.error;
			}
		} else {
			info = "Není žádný soubor ke zpracování";
		}

		updateState({
			info,
		});
	}

	function onDragStart(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
	}

	useSeo({
		title: "Úprava fotek",
		canonical: "gdpr",
	});

	useEffect(() => {
		// skryjeme hlavicku
		document.querySelector("div.header").style.display = "none";
	}, []);

	return <div className="body-background">
		<div className="edit-page page-content top-shadow">
			<h1 className="other-page-title">
				Editace fotek
			</h1>
			<div className="row edit-page__opts">
				{ ALL_OPTS.map(item => <button type="button" className={getClassName(["edit-page__optsBtn", item === state.selName ? "active" : ""])} key={item} onClick={() => updateState({ selName: item })}>{ getName(item) }</button>) }
			</div>
			{ state.info && <p className="edit-page__infoText">{ state.info }</p>}
			<div className="row edit-page__info">
				<button type="button" className="edit-page__optsBtn" onClick={saveChanges}>Uložit změny</button>
				<button type="button" className="edit-page__optsBtn" onClick={sendChanges}>Odeslat změny</button>
				<button type="button" className="edit-page__optsBtn" onClick={exportFile}>Exportovat</button>
				<button type="button" className="edit-page__optsBtn" onClick={deleteAll}>Smazat všechny lokální změny</button>
				<span>Načíst soubor:</span>
				<input type="file" onChange={onChange}></input>
			</div>
			<SortableList onSortEnd={onSortEnd} className="edit-page__items" draggedItemClassName="editPageDragged">
				{ getItems().map(item => <SortableItem key={item.photo}>
					<div className="edit-page__item">
						<img src={item.photo.replace("-#s-", "-s-")} alt={item.uni || item.cs || "-"} draggable={false} onDragStart={onDragStart} />
						<span className="edit-page__desc">
							Pozice: { item.order }<br />
							Stránka: { item.page }
						</span>
						<button className="edit-page__itemDelete" type="button" onClick={() => deletePhoto(item)}>X</button>
					</div>
				</SortableItem>) }
			</SortableList>
		</div>
	</div>;
}
